/**
 * Types for the internal metrics dashboard.
 *
 * Deliberately decoupled from `ReportingEntry`: the aggregator consumes a
 * narrow `MetricsRow` so the data source can move server-side later without
 * touching display code.
 */

import type { ReportingEntryStatus } from "@/shared/cache/slices/reportingEntries";

export const METRIC_STATUSES: ReportingEntryStatus[] = [
	"tracked",
	"exported",
	"deleted",
];

export type MetricStatus = ReportingEntryStatus;

/** Whether a number counts entries or sums hours. */
export type Measure = "count" | "units";

/**
 * The minimum an entry must expose to be aggregated.
 *
 * `original_*` are the v1 provenance fields (present today). The two
 * `*EntryGroup*` / `originalMatterId` fields are the v2 provenance columns —
 * optional because they are null on every row until the backend migration
 * runs, and absent entirely from responses until it ships.
 */
export interface MetricsRow {
	id: number;
	profId: string;
	profName: string;
	date: string;
	status: MetricStatus;
	/**
	 * Seconds of tracked time, or null when the source did not supply one.
	 *
	 * Null is NOT zero: `units` cannot stand in for it, since some firms bill on
	 * 0.1h increments and it is not the elapsed duration. Rows without a
	 * duration are counted but contribute no hours, and are tallied separately
	 * so the gap is visible rather than quietly understating Units mode.
	 */
	seconds: number | null;

	/** "User Input" marks hand-created; null is usually a timer-fill row. */
	application: string | null;

	// ── v1 provenance: "original differs from current" means corrected ──
	matterId: string | null;
	originalMatter: string | null;
	description: string;
	originalDescription: string | null;
	units: number;
	originalUnits: number | null;
	task: string | null;
	originalTask: string | null;
	activity: string | null;
	originalActivity: string | null;

	// ── v2 provenance: written on first USER edit; null means never edited ──
	/**
	 * Group the entry was moved out of, stamped on the attorney's first move.
	 * Non-null = a human regrouped this entry = one grouping misprediction.
	 * The ingest grouper's automatic re-placement does NOT write this, so the
	 * signal is free of system noise.
	 */
	originalEntryGroupId?: string | null;
	/**
	 * Group-matter provenance, stamped on the attorney's first matter edit.
	 * THREE meaningful states — see `MatterProvenance`.
	 */
	originalMatterId?: string | null;
}

/**
 * The three states of `originalMatterId`. The empty string is a real bucket,
 * never collapsed into "no data": it is the case where the system produced no
 * matter at all and the human supplied one — a miss rather than a mistake.
 */
export type MatterProvenance = "untouched" | "system_missed" | "system_wrong";

export const CORRECTION_FIELDS = [
	"matter",
	"narrative",
	"units",
	"task",
	"activity",
	"groupMatter",
	"groupMembership",
] as const;

export type CorrectionField = (typeof CORRECTION_FIELDS)[number];

export const CORRECTION_LABELS: Record<CorrectionField, string> = {
	matter: "Matter",
	narrative: "Narrative",
	units: "Units",
	task: "Task code",
	activity: "Activity code",
	groupMatter: "Group matter",
	groupMembership: "Group membership",
};

export interface StatusTotals {
	count: number;
	seconds: number;
}

/** Everything the dashboard renders, for one attorney or for the firm. */
export interface MetricsBucket {
	profId: string;
	profName: string;

	byStatus: Record<MetricStatus, StatusTotals>;

	/** Per-field correction counts. */
	corrections: Record<CorrectionField, number>;

	/** Matter-provenance split from the v2 group column. */
	matterProvenance: Record<MatterProvenance, number>;

	/** Counts behind the three headline targets. */
	headline: {
		/** Excludes manual and unclassifiable rows. */
		generated: number;
		generatedSeconds: number;
		generatedDeleted: number;
		generatedDeletedSeconds: number;
		exported: number;
		exportedSeconds: number;
		exportedManual: number;
		exportedManualSeconds: number;
		/** Excluded from both sides: capture source unknown. Surfaced, not hidden. */
		unclassified: number;
		/** Counted, but contributing no hours — the source gave no duration. */
		missingDuration: number;
	};

	/** Null for an attorney with no activity. */
	lastActive: string | null;
}


export interface MetricsSummary {
	/** Firm-wide rollup. */
	firm: MetricsBucket;
	/** One bucket per licensed timekeeper, including zero-activity ones. */
	perUser: MetricsBucket[];
	/**
	 * How many rows carried the v2 provenance fields at all. Zero means the
	 * migration has not reached this data yet, so every v2-derived rate must be
	 * suppressed rather than shown as a flattering 0%.
	 */
	provenanceCoverage: number;
	/**
	 * Rows carrying a non-null value, PER FIELD.
	 *
	 * Per-field rather than one shared number because the five v1 `original_*`
	 * columns are populated independently — /admin/user-transactions hardcodes
	 * three of them to literal NULL. A single "any field" count would let one
	 * populated field make its silent siblings render 0%, which reads as
	 * perfect accuracy rather than as missing data.
	 *
	 * v2 fields are deliberately NOT tracked here: their response contract
	 * ships them together, so family-level coverage is correct for those.
	 */
	v1ProvenanceCoverage: Record<CorrectionField, number>;
}
